import React, { forwardRef } from 'react';
import Input, { InputProps } from '@/components/primitives/input';
import styles from './styles.module.scss';
import Typography from '@/components/primitives/typography';

interface Props {
  onPressInput?: () => void;
  placeholder?: string;
  // 새로운 웹 UX를 위한 제어형 입력 지원
  value?: string;
  onChangeText?: (value: string) => void;
  editable?: boolean;
  children?: React.ReactNode; // 입력 바로 아래 DropDown 렌더링을 위한 슬롯
  // 포커스 상태 변경을 부모에게 전달 (입력/드롭다운 wrapper 기준)
  onFocusWithinChange?: (focused: boolean) => void;
}

/**
 * 연결 입력 컴포넌트
 * - 기본(모바일 등): 눌렀을 때 액션시트 오픈
 * - 웹 등 인라인 입력: value/onChangeText/editable 전달 시 텍스트 입력 가능 + children 아래 렌더링
 */
const LinkInput = forwardRef<HTMLInputElement, Props>((props: Props, ref) => {
  const {
    onPressInput,
    placeholder = '피드와 연관된 장소를 검색해서 추가해요!',
    value,
    onChangeText,
    editable,
    children,
    onFocusWithinChange,
  } = props;
  const isIOSWeb = typeof window !== 'undefined' && typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (/(Mac|iPad)/.test(navigator.userAgent) && typeof document !== 'undefined' && 'ontouchend' in document));

  const handlePress = React.useCallback(() => {
    if (!onPressInput) return;
    if (isIOSWeb) {
      try {
        const active = (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);
        active?.blur?.();
      } catch (_) {
      }
      setTimeout(() => onPressInput(), 120);
    } else {
      onPressInput();
    }
  }, [isIOSWeb, onPressInput]);

  // wrapper ref: 입력과 드롭다운을 모두 감싸 포커스 이동을 추적
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const handleFocus = React.useCallback(() => {
    onFocusWithinChange?.(true);
  }, [onFocusWithinChange]);

  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      onFocusWithinChange?.(false);
      return;
    }
    if (next && wrapper.contains(next)) {
      // 포커스가 wrapper 내부로 이동: 유지
      return;
    }
    onFocusWithinChange?.(false);
  }, [onFocusWithinChange]);

  return (
    <div className={`${styles.container}`}
         ref={wrapperRef} onFocus={handleFocus} onBlur={handleBlur}>
      <Input
        ref={ref}
        header={<Typography size="small" weight="bold">연결</Typography>}
        autoCapitalize="none"
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChangeText?.(e.target.value)}
      />
      {children}
    </div>
  );
});

LinkInput.displayName = 'LinkInput';

export default LinkInput;